/**
 * Page Admin - Gestion détaillée d'une inscription (paiements, aides, documents)
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { inscriptionService, aideService, paiementService, documentService, sejourService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { notify } from '../components/NotificationContainer';
import { prompt } from '../components/PromptContainer';
import { confirm } from '../components/ConfirmContainer';
import ParentInfoModal from '../components/ParentInfoModal';
import logger from '../utils/logger';

const AdminInscriptionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inscription, setInscription] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [aides, setAides] = useState([]);
  const [paiements, setPaiements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddAide, setShowAddAide] = useState(false);
  const [newAide, setNewAide] = useState({
    type_aide: 'CAF',
    organisme: '',
    montant: 0
  });
  const [newAideCaf, setNewAideCaf] = useState({
    type_aide: 'CAF',
    organisme: 'CAF',
    montant: 0
  });
  const [showAideCafForm, setShowAideCafForm] = useState(true);
  const [isCafMenuCollapsed, setIsCafMenuCollapsed] = useState(false);
  const [showParentInfoModal, setShowParentInfoModal] = useState(false);
  const [showEditTrancheAge, setShowEditTrancheAge] = useState(false);
  const [tranchesAge, setTranchesAge] = useState([]);

  useEffect(() => {
    if (!user || user.role !== 'ADMIN') {
      navigate('/');
      return;
    }
    loadInscriptionDetails();
  }, [id, user, navigate]);

  const loadInscriptionDetails = async () => {
    try {
      const [inscResp, aidesResp, paiementsResp, documentsResp] = await Promise.all([
        inscriptionService.getOne(id),
        aideService.parInscription(id),
        paiementService.parInscription(id),
        documentService.parInscription(id)
      ]);
      setInscription(inscResp.data);
      setAides(aidesResp.data);
      setPaiements(paiementsResp.data);
      setDocuments(documentsResp.data);

      // Charger les tranches d'âge du séjour
      if (inscResp.data.sejour_details && inscResp.data.sejour_details.id) {
        try {
          const sejourResp = await sejourService.getOne(inscResp.data.sejour_details.id);
          if (sejourResp.data.tranches_prix && sejourResp.data.tranches_prix.length > 0) {
            setTranchesAge(sejourResp.data.tranches_prix);
          }
        } catch (error) {
          logger.error('Erreur lors du chargement des tranches d\'âge:', error);
        }
      }
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAide = async () => {
    if (!newAide.organisme || newAide.montant <= 0) {
      notify.warning('Veuillez remplir tous les champs');
      return;
    }

    try {
      await aideService.create({
        inscription: id,
        ...newAide
      });
      notify.success('Aide ajoutée avec succès');
      setShowAddAide(false);
      setNewAide({ type_aide: 'CAF', organisme: '', montant: 0 });
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de l\'ajout de l\'aide');
    }
  };

  const handleStatutCafChange = async (statut) => {
    // Validation avant changement de statut
    let confirmMessage = '';
    if (statut === 'AIDE_POSSIBLE') {
      confirmMessage = 'Confirmer que l\'aide CAF est possible ?';
    } else if (statut === 'AUCUNE_AIDE') {
      confirmMessage = 'Confirmer qu\'aucune aide CAF n\'est possible ? Un email sera envoyé au parent.';
    }

    if (confirmMessage && !await confirm.ask(confirmMessage, 'Confirmation')) {
      return;
    }

    try {
      await inscriptionService.setStatutVerificationCaf(id, statut);

      if (statut === 'AUCUNE_AIDE') {
        notify.info('Statut enregistré. Un email sera envoyé au parent.');
      } else if (statut === 'AIDE_POSSIBLE') {
        notify.success('Aide CAF possible ! Vous pouvez maintenant créer l\'aide.');
        setShowAideCafForm(true); // Afficher le formulaire
      }

      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la mise à jour du statut');
    }
  };

  const handleCreateAideCaf = async () => {
    if (!newAideCaf.organisme || newAideCaf.montant <= 0) {
      notify.warning('Veuillez remplir tous les champs');
      return;
    }

    try {
      await aideService.create({
        inscription: id,
        ...newAideCaf
      });
      notify.success('Aide CAF créée avec succès');
      setNewAideCaf({ type_aide: 'CAF', organisme: 'CAF', montant: 0 });
      setShowAideCafForm(false); // Masquer le formulaire après création
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la création de l\'aide');
    }
  };

  const handleValiderAide = async (aideId) => {
    if (!await confirm.ask('Êtes-vous sûr de vouloir valider cette aide ?', 'Valider l\'aide')) return;

    try {
      await aideService.valider(aideId);
      notify.success('Aide validée');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la validation');
    }
  };

  const handleRefuserAide = async (aideId) => {
    const motif = await prompt.askRefusalReason();
    if (!motif) return;

    try {
      await aideService.refuser(aideId, motif);
      notify.success('Aide refusée');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du refus');
    }
  };

  const handleDeleteAide = async (aideId) => {
    if (!await confirm.delete('Êtes-vous sûr de vouloir supprimer cette aide ?', 'Supprimer l\'aide')) return;

    try {
      await aideService.delete(aideId);
      notify.success('Aide supprimée');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la suppression');
    }
  };

  const handleValiderPaiement = async (paiementId) => {
    if (!await confirm.ask('Êtes-vous sûr de vouloir valider ce paiement ?', 'Valider le paiement')) return;

    try {
      await paiementService.valider(paiementId);
      notify.success('Paiement validé');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la validation');
    }
  };

  const handleRefuserPaiement = async (paiementId) => {
    const motif = await prompt.askRefusalReason();
    if (!motif) return;

    try {
      await paiementService.refuser(paiementId, motif);
      notify.success('Paiement refusé');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du refus');
    }
  };

  const handleAnnulerValidationPaiement = async (paiementId) => {
    if (!await confirm.warning('Annuler la validation de ce paiement et le remettre en attente ?', 'Annuler la validation')) return;

    try {
      await paiementService.annulerValidation(paiementId);
      notify.success('Validation du paiement annulée');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de l\'annulation de la validation');
    }
  };

  const handleValiderDocument = async (documentId) => {
    const commentaire = await prompt.askComment('Ajouter un commentaire (optionnel) :', '', false);
    if (commentaire === null) return;

    if (!await confirm.ask('Êtes-vous sûr de vouloir valider ce document ?', 'Valider le document')) return;

    try {
      await documentService.valider(documentId, commentaire || '');
      notify.success('Document validé');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la validation');
    }
  };

  const handleRefuserDocument = async (documentId) => {
    const commentaire = await prompt.askRefusalReason();
    if (!commentaire) return;

    try {
      await documentService.refuser(documentId, commentaire);
      notify.success('Document refusé');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du refus');
    }
  };

  const handleMarquerNonObtenu = async (documentId) => {
    const commentaire = await prompt.askComment('Ajouter un commentaire (optionnel) :', '', false);
    if (commentaire === null) return;

    if (!await confirm.warning('Marquer ce document comme non obtenu ?', 'Document non obtenu')) return;

    try {
      await documentService.marquerNonObtenu(documentId, commentaire);
      notify.success('Document marqué comme non obtenu');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du marquage');
    }
  };

  const handleAnnulerValidationDocument = async (documentId) => {
    if (!await confirm.warning('Annuler la validation de ce document et le remettre en attente ?', 'Annuler la validation')) return;

    try {
      await documentService.annulerValidation(documentId);
      notify.success('Validation du document annulée');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de l\'annulation de la validation');
    }
  };

  const handleValiderAccordAide = async (aideId) => {
    const commentaire = await prompt.askComment('Ajouter un commentaire (optionnel) :', '', false);
    if (commentaire === null) return;

    if (!await confirm.ask('Êtes-vous sûr de vouloir valider l\'accord avec l\'organisme ?', 'Valider l\'accord')) return;

    try {
      await aideService.validerAccord(aideId, commentaire);
      notify.success('Accord validé');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la validation de l\'accord');
    }
  };

  const handleRefuserAccordAide = async (aideId) => {
    const commentaire = await prompt.askComment('Motif du refus d\'accord (optionnel) :', '', false);
    if (commentaire === null) return;

    if (!await confirm.danger('Êtes-vous sûr de vouloir refuser l\'accord avec l\'organisme ?', 'Refuser l\'accord')) return;

    try {
      await aideService.refuserAccord(aideId, commentaire);
      notify.success('Accord refusé');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du refus de l\'accord');
    }
  };

  const handleValiderPaiementAide = async (aideId) => {
    const commentaire = await prompt.askComment('Ajouter un commentaire (optionnel) :', '', false);
    if (commentaire === null) return;

    if (!await confirm.ask('Confirmer que le paiement a été reçu ?', 'Valider le paiement')) return;

    try {
      await aideService.validerPaiement(aideId, commentaire);
      notify.success('Paiement validé');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la validation du paiement');
    }
  };

  const handleRefuserPaiementAide = async (aideId) => {
    const commentaire = await prompt.askComment('Motif (optionnel) :', '', false);
    if (commentaire === null) return;

    if (!await confirm.warning('Marquer ce paiement comme non reçu ?', 'Paiement non reçu')) return;

    try {
      await aideService.refuserPaiement(aideId, commentaire);
      notify.success('Paiement marqué comme non reçu');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du refus du paiement');
    }
  };

  const handleAnnulerAccordAide = async (aideId) => {
    if (!await confirm.warning('Annuler le statut d\'accord et le remettre en attente ?', 'Annuler l\'accord')) return;

    try {
      await aideService.annulerAccord(aideId);
      notify.success('Accord annulé, statut remis en attente');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de l\'annulation de l\'accord');
    }
  };

  const handleAnnulerPaiementAide = async (aideId) => {
    if (!await confirm.warning('Annuler le statut de paiement et le remettre en attente ?', 'Annuler le paiement')) return;

    try {
      await aideService.annulerPaiement(aideId);
      notify.success('Paiement annulé, statut remis en attente');
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de l\'annulation du paiement');
    }
  };

  const handleUpdateTrancheAge = async (newTrancheId) => {
    if (!newTrancheId) {
      notify.warning('Veuillez sélectionner une tranche d\'âge');
      return;
    }

    // Trouver les détails de la nouvelle tranche
    const newTranche = tranchesAge.find(t => t.id === parseInt(newTrancheId));
    if (!newTranche) {
      notify.error('Tranche d\'âge introuvable');
      return;
    }

    const confirmMessage = `Confirmer le changement de tranche d'âge ?\n\nNouvelle tranche : ${newTranche.age_min}-${newTranche.age_max} ans\nNouveau prix : ${newTranche.prix}€\n\nLe montant total de l'inscription sera mis à jour automatiquement.`;

    if (!await confirm.ask(confirmMessage, 'Modifier la tranche d\'âge')) return;

    try {
      await inscriptionService.updateTrancheAge(id, newTrancheId);
      notify.success('Tranche d\'âge modifiée avec succès');
      setShowEditTrancheAge(false);
      loadInscriptionDetails();
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error(error.response?.data?.error || 'Erreur lors de la modification de la tranche d\'âge');
    }
  };

  const handleValiderPartenaire = async () => {
    if (!await confirm.ask('Êtes-vous sûr de vouloir valider cette inscription partenaire ?', 'Valider l\'inscription')) return;

    try {
      await inscriptionService.validerPartenaire(id);

      // Mettre à jour l'état local immédiatement
      setInscription(prev => ({
        ...prev,
        partenaire_valide: true,
        statut: 'CONFIRMEE'
      }));

      notify.success('Inscription partenaire validée avec succès');
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors de la validation');
    }
  };

  const handleRefuserPartenaire = async () => {
    const motif = await prompt.askRefusalReason();
    if (!motif) return;

    try {
      await inscriptionService.refuserPartenaire(id, motif);

      // Mettre à jour l'état local immédiatement
      setInscription(prev => ({
        ...prev,
        statut: 'ANNULEE'
      }));

      notify.success('Inscription partenaire refusée');
    } catch (error) {
      logger.error('Erreur:', error);
      notify.error('Erreur lors du refus');
    }
  };

  if (loading) return <div className="text-center py-12">Chargement...</div>;

  if (!inscription) return <div className="text-center py-12">Inscription non trouvée</div>;

  return (
    <div className="max-w-7xl mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
      {/* En-tête */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/dashboard')}
          className="mb-4 text-primary-600 hover:text-primary-800 flex items-center text-sm sm:text-base"
        >
          ← Retour au tableau de bord
        </button>
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900">
            Gestion de l'inscription - {inscription.enfant_details?.prenom} {inscription.enfant_details?.nom}
          </h1>
          <button
            onClick={() => navigate(`/inscriptions/${id}`)}
            className="bg-blue-600 text-white px-4 py-3 sm:py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center shadow"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
            <span className="hidden sm:inline">Voir comme parent</span>
            <span className="sm:hidden">Vue parent</span>
          </button>
        </div>
      </div>

      <div className="space-y-6">
        {/* Validation Partenaire - Alerte si en attente */}
        {inscription.via_partenaire && inscription.statut === 'EN_ATTENTE_VALIDATION_PARTENAIRE' && !inscription.partenaire_valide && (
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 sm:p-6 rounded-lg shadow">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center mb-2">
                  <svg className="h-6 w-6 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <h3 className="text-lg font-bold text-yellow-900">Inscription via partenaire social - Validation requise</h3>
                </div>
                <p className="text-sm text-yellow-800 mb-4">
                  Cette inscription a été effectuée via un partenaire social et nécessite votre validation avant d'être confirmée.
                </p>
                {inscription.partenaire_affilie && (
                  <p className="text-sm text-yellow-700">
                    <strong>Partenaire :</strong> {inscription.partenaire_affilie}
                  </p>
                )}
              </div>
              <div className="flex flex-col sm:flex-row gap-3 sm:ml-4 mt-4 sm:mt-0">
                <button
                  onClick={handleRefuserPartenaire}
                  className="px-4 py-3 sm:py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center justify-center font-medium"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Refuser
                </button>
                <button
                  onClick={handleValiderPartenaire}
                  className="px-4 py-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center font-medium"
                >
                  <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Valider l'inscription
                </button>
              </div>
            </div>
          </div>
        )}


        {/* Informations inscription */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h3 className="text-lg font-medium mb-4">Informations de l'inscription</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-4">
            <div>
              <p className="text-sm text-gray-600">Enfant</p>
              <p className="font-medium">{inscription.enfant_details?.prenom} {inscription.enfant_details?.nom}</p>
              <p className="text-xs text-gray-500">Né(e) le {new Date(inscription.enfant_details?.date_naissance).toLocaleDateString('fr-FR')}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Séjour</p>
              <p className="font-medium">{inscription.sejour_details?.titre}</p>
            </div>
            <div>
              <p className="text-sm text-gray-600">Tranche d'âge inscrite</p>
              <div className="flex items-center gap-2">
                {inscription.tranche_prix_details ? (
                  <p className="font-medium">{inscription.tranche_prix_details.age_min}-{inscription.tranche_prix_details.age_max} ans</p>
                ) : inscription.inscription_age_min !== null && inscription.inscription_age_max !== null ? (
                  <div className="flex flex-col">
                    <p className="font-medium">{inscription.inscription_age_min}-{inscription.inscription_age_max} ans</p>
                    <span className="text-xs text-orange-600 italic flex items-center gap-1">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
                      Tranche supprimée (archivé)
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-400 italic">Non définie</p>
                )}
                {tranchesAge.length > 0 && (
                  <button
                    onClick={() => setShowEditTrancheAge(!showEditTrancheAge)}
                    className="text-primary-600 hover:text-primary-800 text-xs underline"
                    title="Modifier la tranche d'âge"
                  >
                    Modifier
                  </button>
                )}
              </div>
            </div>
            <div>
              <p className="text-sm text-gray-600">Parent</p>
              <button
                onClick={() => setShowParentInfoModal(true)}
                className="font-medium text-primary-600 hover:text-primary-700 hover:underline cursor-pointer text-left"
              >
                {inscription.enfant_details?.parent_name || <span className="text-gray-400">Non renseigné</span>}
              </button>
            </div>
            <div>
              <p className="text-sm text-gray-600">Date d'inscription</p>
              <p className="font-medium">{new Date(inscription.date_inscription).toLocaleDateString('fr-FR')}</p>
              <p className="text-xs text-gray-500">{new Date(inscription.date_inscription).toLocaleTimeString('fr-FR')}</p>
            </div>
          </div>

          {/* Interface de modification de la tranche d'âge - En pleine largeur */}
          {showEditTrancheAge && tranchesAge.length > 1 && (
            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm font-medium text-blue-900 mb-3">Modifier la tranche d'âge</p>
              <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
                <div className="flex-1 w-full">
                  <label htmlFor="select-tranche-age" className="block text-sm text-gray-700 mb-1">
                    Nouvelle tranche d'âge
                  </label>
                  <select
                    id="select-tranche-age"
                    defaultValue={inscription.tranche_prix_details?.id || ''}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Sélectionner une tranche</option>
                    {tranchesAge.map((tranche) => (
                      <option key={tranche.id} value={tranche.id}>
                        {tranche.age_min}-{tranche.age_max} ans ({tranche.prix}€)
                      </option>
                    ))}
                  </select>
                </div>
                <div className="flex gap-2 w-full sm:w-auto">
                  <button
                    onClick={() => {
                      const selectElement = document.getElementById('select-tranche-age');
                      handleUpdateTrancheAge(selectElement.value);
                    }}
                    className="flex-1 sm:flex-none bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 text-sm font-medium"
                  >
                    Valider
                  </button>
                  <button
                    onClick={() => setShowEditTrancheAge(false)}
                    className="flex-1 sm:flex-none bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 text-sm font-medium"
                  >
                    Annuler
                  </button>
                </div>
              </div>
              <p className="text-xs text-gray-600 mt-3">
                💡 Le montant total de l'inscription sera automatiquement mis à jour selon la nouvelle tranche.
              </p>
            </div>
          )}

          {/* Consentement RGPD santé - Affichage discret */}
          <div className="border-t pt-3 mt-3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-600">Consentement RGPD santé</p>
              {inscription.health_data_consent && inscription.health_consent_date ? (
                <div className="text-right">
                  <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                    ✓ Oui
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {new Date(inscription.health_consent_date).toLocaleDateString('fr-FR', {
                      day: '2-digit',
                      month: '2-digit',
                      year: 'numeric'
                    })} {new Date(inscription.health_consent_date).toLocaleTimeString('fr-FR', {
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600">
                  ✗ Non donné
                </span>
              )}
            </div>
          </div>

          <div className="border-t pt-4 mt-4">
            <h4 className="text-sm font-medium text-gray-700 mb-3">Récapitulatif financier</h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <p className="text-xs text-gray-600 mb-1">Prix du séjour</p>
                <p className="font-bold text-lg">{inscription.montant_total} €</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Total des aides</p>
                <p className="font-bold text-lg text-green-600">-{inscription.total_aides || 0} €</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Montant payé</p>
                <p className="font-bold text-lg text-blue-600">{inscription.montant_paye} €</p>
              </div>
              <div>
                <p className="text-xs text-gray-600 mb-1">Reste à payer</p>
                <p className="font-bold text-lg text-orange-600">{inscription.solde_restant} €</p>
              </div>
            </div>
          </div>
        </div>

        {/* Représentant légal */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h3 className="text-lg font-medium mb-4">Représentant légal et informations sociales</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700 border-b pb-2">Représentant légal</h4>
              <div>
                <p className="text-xs text-gray-500">Nom et prénom</p>
                <p className="font-medium">{inscription.responsable_prenom} {inscription.responsable_nom || <span className="text-gray-400">Non renseigné</span>}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="font-medium">{inscription.responsable_email || <span className="text-gray-400">Non renseigné</span>}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Téléphone</p>
                <p className="font-medium">{inscription.responsable_telephone || <span className="text-gray-400">Non renseigné</span>}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Adresse</p>
                <p className="font-medium">{inscription.responsable_adresse || <span className="text-gray-400">Non renseignée</span>}</p>
              </div>
            </div>

            <div className="space-y-3">
              <h4 className="font-medium text-sm text-gray-700 border-b pb-2">Volet social</h4>
              <div>
                <p className="text-xs text-gray-500">Numéro allocataire CAF/MSA</p>
                <p className="font-medium">{inscription.numero_msa_caf || <span className="text-gray-400">Non renseigné</span>}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Partenaire affilié</p>
                <p className="font-medium">{inscription.partenaire_affilie || <span className="text-gray-400">Non renseigné</span>}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Travailleur social référent</p>
                <p className="font-medium">{inscription.travailleur_social_referent || <span className="text-gray-400">Non renseigné</span>}</p>
              </div>
            </div>
          </div>

          {/* Remarques des parents */}
          {inscription.remarques && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <h4 className="font-medium text-sm text-gray-700 mb-2 flex items-center">
                <svg className="h-5 w-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                </svg>
                Remarques des parents
              </h4>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{inscription.remarques}</p>
              </div>
            </div>
          )}
        </div>

        {/* Gestion des aides */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3 mb-4">
            <h3 className="text-lg font-medium">Aides financières</h3>
            <button
              onClick={() => setShowAddAide(!showAddAide)}
              className="bg-green-600 text-white px-4 py-3 sm:py-2 rounded hover:bg-green-700"
            >
              {showAddAide ? '✖ Annuler' : '+ Ajouter une aide'}
            </button>
          </div>

          {/* Bloc vérification CAF */}
          {inscription && inscription.numero_msa_caf ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              {/* Header avec bouton de repliement */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3 flex-1">
                  <div className="flex-shrink-0">
                    <svg className="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-medium text-blue-900 mb-1">Vérification éligibilité aide CAF</h4>
                    <p className="text-sm text-blue-800">
                      Numéro allocataire CAF : <strong>{inscription.numero_msa_caf}</strong>
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsCafMenuCollapsed(!isCafMenuCollapsed)}
                  className="flex-shrink-0 text-blue-600 hover:text-blue-800 transition-colors p-1"
                  title={isCafMenuCollapsed ? "Déplier" : "Replier"}
                >
                  <svg
                    className={`h-5 w-5 transform transition-transform ${isCafMenuCollapsed ? '' : 'rotate-180'}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              </div>

              {/* Contenu déplié */}
              {!isCafMenuCollapsed && (
                <div className="pl-9">
                  {/* Radio buttons Oui/Non */}
                  <div className="space-y-2 mb-3">
                    <p className="text-sm font-medium text-blue-900">Aide CAF possible pour cette inscription ?</p>
                    <div className="flex gap-4">
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="statut_caf"
                          value="AIDE_POSSIBLE"
                          checked={inscription.statut_verification_caf === 'AIDE_POSSIBLE'}
                          onChange={() => handleStatutCafChange('AIDE_POSSIBLE')}
                          className="mr-2"
                        />
                        <span className="text-sm">Oui, aide possible</span>
                      </label>
                      <label className="flex items-center cursor-pointer">
                        <input
                          type="radio"
                          name="statut_caf"
                          value="AUCUNE_AIDE"
                          checked={inscription.statut_verification_caf === 'AUCUNE_AIDE'}
                          onChange={() => handleStatutCafChange('AUCUNE_AIDE')}
                          className="mr-2"
                        />
                        <span className="text-sm">Non, aucune aide possible</span>
                      </label>
                    </div>
                  </div>

                  {/* Statut actuel */}
                  <div className="mb-3">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${inscription.statut_verification_caf === 'AIDE_POSSIBLE' ? 'bg-green-100 text-green-800' :
                      inscription.statut_verification_caf === 'AUCUNE_AIDE' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                      {inscription.statut_verification_caf === 'AIDE_POSSIBLE' ? '✓ Aide CAF possible' :
                        inscription.statut_verification_caf === 'AUCUNE_AIDE' ? '✗ Aucune aide CAF possible' :
                          '⏳ En attente de vérification'}
                    </span>
                  </div>

                  {/* Formulaire création aide CAF si "Oui" sélectionné */}
                  {inscription.statut_verification_caf === 'AIDE_POSSIBLE' && (
                    showAideCafForm ? (
                      <div className="mt-4 bg-white border border-green-300 rounded-lg p-4">
                        <h5 className="font-medium text-green-900 mb-3">Créer une aide CAF</h5>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Organisme</label>
                            <input
                              type="text"
                              value={newAideCaf.organisme}
                              onChange={(e) => setNewAideCaf({ ...newAideCaf, organisme: e.target.value })}
                              className="w-full border rounded px-3 py-2"
                              placeholder="CAF, MSA..."
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Montant (€)</label>
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={newAideCaf.montant}
                              onChange={(e) => setNewAideCaf({ ...newAideCaf, montant: parseFloat(e.target.value) || 0 })}
                              className="w-full border rounded px-3 py-2"
                            />
                          </div>
                        </div>
                        <button
                          onClick={handleCreateAideCaf}
                          className="mt-3 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors"
                        >
                          Créer l'aide CAF
                        </button>
                      </div>
                    ) : (
                      <div className="mt-3">
                        <button
                          onClick={() => setShowAideCafForm(true)}
                          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition-colors text-sm"
                        >
                          + Ajouter une aide CAF
                        </button>
                      </div>
                    )
                  )}
                </div>
              )}
            </div>
          ) : inscription && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4">
              <div className="flex items-start">
                <svg className="h-5 w-5 text-gray-400 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-gray-600">
                  ℹ️ Numéro allocataire CAF non renseigné par le parent
                </p>
              </div>
            </div>
          )}

          {/* Formulaire d'ajout d'aide */}
          {showAddAide && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Type d'aide</label>
                  <select
                    value={newAide.type_aide}
                    onChange={(e) => setNewAide({ ...newAide, type_aide: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  >
                    <option value="CAF">CAF / MSA</option>
                    <option value="COMMUNE">Commune</option>
                    <option value="ASSOCIATION">Association</option>
                    <option value="BOURSE">Bourse</option>
                    <option value="AUTRE">Autre</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Organisme</label>
                  <input
                    type="text"
                    placeholder="Nom de l'organisme"
                    value={newAide.organisme}
                    onChange={(e) => setNewAide({ ...newAide, organisme: e.target.value })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Montant (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newAide.montant}
                    onChange={(e) => setNewAide({ ...newAide, montant: parseFloat(e.target.value) || 0 })}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              </div>
              <button
                onClick={handleAddAide}
                className="mt-3 bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Enregistrer l'aide
              </button>
            </div>
          )}

          {/* Liste des aides */}
          <div className="space-y-3">
            {aides.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucune aide enregistrée</p>
            ) : (
              aides.map((aide) => (
                <div key={aide.id} className="border border-gray-200 p-4 rounded-lg">
                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start mb-3 gap-3">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 mb-2">
                        <p className="font-medium text-lg">{aide.montant} €</p>
                        <span className="text-sm text-gray-600">({aide.type_aide})</span>
                        <span className="text-sm font-medium text-gray-700">{aide.organisme}</span>
                      </div>
                      {aide.commentaire && (
                        <p className="text-sm text-gray-500 mt-1">{aide.commentaire}</p>
                      )}
                    </div>
                    <div className="w-full sm:w-auto sm:ml-4">
                      <button
                        onClick={() => handleDeleteAide(aide.id)}
                        className="w-full sm:w-auto bg-gray-600 text-white px-3 py-2 sm:py-1 rounded hover:bg-gray-700 text-sm"
                      >
                        🗑️ Supprimer
                      </button>
                    </div>
                  </div>

                  {/* Statuts et validations */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 pt-3 border-t border-gray-200">
                    {/* Accord organisme */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Accord organisme</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${aide.statut_accord === 'ACCORDE' ? 'bg-blue-100 text-blue-800' :
                        aide.statut_accord === 'REFUSE' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                        {aide.statut_accord === 'ACCORDE' ? '✓ Accord obtenu' :
                          aide.statut_accord === 'REFUSE' ? '✗ Refusé' :
                            '⏳ En attente'}
                      </span>
                      {aide.statut_accord === 'EN_ATTENTE' && (
                        <div className="flex flex-col sm:flex-row gap-2 sm:gap-1 mt-2">
                          <button
                            onClick={() => handleValiderAccordAide(aide.id)}
                            className="bg-blue-600 text-white px-3 py-2 sm:px-2 sm:py-1 rounded hover:bg-blue-700 text-sm sm:text-xs"
                          >
                            ✓ Accord
                          </button>
                          <button
                            onClick={() => handleRefuserAccordAide(aide.id)}
                            className="bg-red-600 text-white px-3 py-2 sm:px-2 sm:py-1 rounded hover:bg-red-700 text-sm sm:text-xs"
                          >
                            ✗ Refus
                          </button>
                        </div>
                      )}
                      {(aide.statut_accord === 'ACCORDE' || aide.statut_accord === 'REFUSE') && (
                        <button
                          onClick={() => handleAnnulerAccordAide(aide.id)}
                          className="w-full sm:w-auto bg-gray-500 text-white px-3 py-2 sm:px-2 sm:py-1 rounded hover:bg-gray-600 text-sm sm:text-xs mt-2"
                        >
                          ↺ Annuler
                        </button>
                      )}
                    </div>

                    {/* Paiement effectif */}
                    <div>
                      <p className="text-xs text-gray-500 mb-1">Paiement effectif</p>
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${aide.statut_paiement === 'PAYE' ? 'bg-green-100 text-green-800' :
                        aide.statut_paiement === 'NON_PAYE' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                        {aide.statut_paiement === 'PAYE' ? '✓ Payé' :
                          aide.statut_paiement === 'NON_PAYE' ? '✗ Non payé' :
                            '⏳ En attente'}
                      </span>
                      {aide.statut_paiement === 'EN_ATTENTE' && (
                        <>
                          {aide.statut_accord === 'ACCORDE' ? (
                            <div className="flex flex-col sm:flex-row gap-2 sm:gap-1 mt-2">
                              <button
                                onClick={() => handleValiderPaiementAide(aide.id)}
                                className="bg-green-600 text-white px-3 py-2 sm:px-2 sm:py-1 rounded hover:bg-green-700 text-sm sm:text-xs"
                              >
                                ✓ Payé
                              </button>
                              <button
                                onClick={() => handleRefuserPaiementAide(aide.id)}
                                className="bg-orange-600 text-white px-3 py-2 sm:px-2 sm:py-1 rounded hover:bg-orange-700 text-sm sm:text-xs"
                              >
                                ✗ Non payé
                              </button>
                            </div>
                          ) : (
                            <div className="mt-2 text-xs text-orange-600 italic">
                              ⚠️ Accord organisme requis pour valider le paiement
                            </div>
                          )}
                        </>
                      )}
                      {(aide.statut_paiement === 'PAYE' || aide.statut_paiement === 'NON_PAYE') && (
                        <button
                          onClick={() => handleAnnulerPaiementAide(aide.id)}
                          className="w-full sm:w-auto bg-gray-500 text-white px-3 py-2 sm:px-2 sm:py-1 rounded hover:bg-gray-600 text-sm sm:text-xs mt-2"
                        >
                          ↺ Annuler
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Gestion des paiements */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h3 className="text-lg font-medium mb-4">Paiements de la famille</h3>
          <div className="space-y-3">
            {paiements.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucun paiement enregistré</p>
            ) : (
              paiements.map((paiement) => (
                <div key={paiement.id} className="border border-gray-200 p-4 rounded-lg">
                  <div className="flex flex-col gap-4">
                    <div className="flex-1">
                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3">
                        <p className="font-medium text-lg">{paiement.montant} €</p>
                        <span className="text-sm text-gray-600">({paiement.mode_paiement.replace('_', ' ')})</span>
                        <span className="text-xs bg-gray-100 px-2 py-1 rounded inline-block w-fit">
                          Échéance {paiement.numero_echeance}
                        </span>
                      </div>
                      {paiement.date_echeance && (
                        <p className="text-sm text-gray-600 mt-1">
                          Date d'échéance : {new Date(paiement.date_echeance).toLocaleDateString('fr-FR')}
                        </p>
                      )}
                      {paiement.reference && (
                        <p className="text-sm text-gray-600">Référence : {paiement.reference}</p>
                      )}
                      {paiement.commentaire && (
                        <p className="text-sm text-gray-500 mt-1">{paiement.commentaire}</p>
                      )}
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                      <span className={`px-4 py-2 rounded-full text-sm font-medium text-center ${paiement.statut === 'VALIDE' ? 'bg-green-100 text-green-800' :
                        paiement.statut === 'REFUSE' ? 'bg-red-100 text-red-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                        {paiement.statut === 'VALIDE' ? '✓ Validé' :
                          paiement.statut === 'REFUSE' ? '✗ Refusé' :
                            '⏳ En attente'}
                      </span>
                      {paiement.statut === 'EN_ATTENTE' && (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            onClick={() => handleValiderPaiement(paiement.id)}
                            className="bg-green-600 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-green-700 text-sm"
                          >
                            ✓ Valider
                          </button>
                          <button
                            onClick={() => handleRefuserPaiement(paiement.id)}
                            className="bg-red-600 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-red-700 text-sm"
                          >
                            ✗ Refuser
                          </button>
                        </div>
                      )}
                      {(paiement.statut === 'VALIDE' || paiement.statut === 'REFUSE') && (
                        <button
                          onClick={() => handleAnnulerValidationPaiement(paiement.id)}
                          className="bg-gray-500 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-gray-600 text-sm"
                        >
                          ↺ Annuler la validation
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Gestion des documents */}
        <div className="bg-white shadow rounded-lg p-4 sm:p-6">
          <h3 className="text-lg font-medium mb-4">Documents transmis par la famille</h3>
          <div className="space-y-3">
            {documents.length === 0 ? (
              <p className="text-gray-500 text-sm">Aucun document transmis</p>
            ) : (
              <>
                {/* Documents obligatoires */}
                {documents.filter(d => d.type_document !== 'AUTRE').map((document) => (
                  <div key={document.id} className="border border-gray-200 p-4 rounded-lg">
                    <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                          <h4 className="font-medium text-lg">{document.titre}</h4>
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded inline-block w-fit">
                            {document.type_document.replace('_', ' ')}
                          </span>
                        </div>
                        {document.description && (
                          <p className="text-sm text-gray-600 mb-2">{document.description}</p>
                        )}
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-sm text-gray-500">
                          <span>📅 {new Date(document.date_upload).toLocaleDateString('fr-FR')}</span>
                          <a
                            href={document.fichier}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary-600 hover:text-primary-800"
                          >
                            📄 Voir le document
                          </a>
                        </div>
                        {document.commentaire_validation && (
                          <div className="mt-2 p-2 bg-gray-50 rounded">
                            <p className="text-sm text-gray-600">
                              <strong>Commentaire admin :</strong> {document.commentaire_validation}
                            </p>
                            {document.valide_par_nom && (
                              <p className="text-xs text-gray-500 mt-1">
                                par {document.valide_par_nom} le {new Date(document.date_validation).toLocaleDateString('fr-FR')}
                              </p>
                            )}
                          </div>
                        )}
                      </div>
                      <div className="sm:ml-4 flex flex-col sm:items-end gap-3">
                        <span className={`px-4 py-2 rounded-full text-sm font-medium text-center ${document.statut_validation === 'VALIDE' ? 'bg-green-100 text-green-800' :
                          document.statut_validation === 'REFUSE' ? 'bg-red-100 text-red-800' :
                            document.statut_validation === 'NON_OBTENU' ? 'bg-blue-100 text-blue-800' :
                              'bg-yellow-100 text-yellow-800'
                          }`}>
                          {document.statut_validation === 'VALIDE' ? '✓ Validé' :
                            document.statut_validation === 'REFUSE' ? '✗ Refusé' :
                              document.statut_validation === 'NON_OBTENU' ? '⊘ Non obtenu' :
                                '⏳ En attente'}
                        </span>
                        {document.statut_validation === 'EN_ATTENTE' && (
                          <div className="flex flex-col gap-2 w-full">
                            <div className="flex flex-col sm:flex-row gap-2">
                              <button
                                onClick={() => handleValiderDocument(document.id)}
                                className="bg-green-600 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-green-700 text-sm"
                              >
                                ✓ Valider
                              </button>
                              <button
                                onClick={() => handleRefuserDocument(document.id)}
                                className="bg-red-600 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-red-700 text-sm"
                              >
                                ✗ Refuser
                              </button>
                            </div>
                            {document.type_document === 'ATTESTATION_AISANCE_AQUATIQUE' && (
                              <button
                                onClick={() => handleMarquerNonObtenu(document.id)}
                                className="bg-blue-600 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-blue-700 text-sm"
                              >
                                ⊘ Non obtenu
                              </button>
                            )}
                          </div>
                        )}
                        {(document.statut_validation === 'VALIDE' || document.statut_validation === 'REFUSE' || document.statut_validation === 'NON_OBTENU') && (
                          <button
                            onClick={() => handleAnnulerValidationDocument(document.id)}
                            className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-gray-600 text-sm"
                          >
                            ↺ Annuler la validation
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Documents autres */}
                {documents.filter(d => d.type_document === 'AUTRE').length > 0 && (
                  <div className="mt-6 pt-6 border-t border-gray-300">
                    <h4 className="text-md font-semibold text-gray-900 mb-4">Documents autres</h4>
                    <div className="space-y-4">
                      {documents
                        .filter(d => d.type_document === 'AUTRE')
                        .sort((a, b) => (a.numero_document_autre || 0) - (b.numero_document_autre || 0))
                        .map((document) => (
                          <div key={document.id} className="border border-purple-200 p-4 rounded-lg bg-purple-50">
                            <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
                              <div className="flex-1">
                                <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-3 mb-2">
                                  <h4 className="font-medium text-lg">{document.titre}</h4>
                                  <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded font-medium inline-block w-fit">
                                    Document autre {document.numero_document_autre}
                                  </span>
                                </div>
                                {document.commentaire_parent && (
                                  <div className="mb-2 p-2 bg-blue-50 border border-blue-200 rounded">
                                    <p className="text-sm text-blue-900">
                                      <strong>💬 Commentaire du parent :</strong> {document.commentaire_parent}
                                    </p>
                                  </div>
                                )}
                                <div className="flex items-center space-x-4 text-sm text-gray-600">
                                  <span>📅 {new Date(document.date_upload).toLocaleDateString('fr-FR')}</span>
                                  <a
                                    href={document.fichier}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-primary-600 hover:text-primary-800 font-medium"
                                  >
                                    📄 Voir le document
                                  </a>
                                </div>
                                {document.commentaire_validation && (
                                  <div className="mt-2 p-2 bg-gray-100 rounded">
                                    <p className="text-sm text-gray-700">
                                      <strong>✍️ Commentaire admin :</strong> {document.commentaire_validation}
                                    </p>
                                    {document.valide_par_nom && (
                                      <p className="text-xs text-gray-500 mt-1">
                                        par {document.valide_par_nom} le {new Date(document.date_validation).toLocaleDateString('fr-FR')}
                                      </p>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="sm:ml-4 flex flex-col sm:items-end gap-3">
                                <span className={`px-4 py-2 rounded-full text-sm font-medium text-center ${document.statut_validation === 'VALIDE' ? 'bg-green-100 text-green-800' :
                                  document.statut_validation === 'REFUSE' ? 'bg-red-100 text-red-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }`}>
                                  {document.statut_validation === 'VALIDE' ? '✓ Validé' :
                                    document.statut_validation === 'REFUSE' ? '✗ Refusé' :
                                      '⏳ En attente'}
                                </span>
                                {document.statut_validation === 'EN_ATTENTE' && (
                                  <div className="flex flex-col sm:flex-row gap-2">
                                    <button
                                      onClick={() => handleValiderDocument(document.id)}
                                      className="bg-green-600 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-green-700 text-sm"
                                    >
                                      ✓ Valider
                                    </button>
                                    <button
                                      onClick={() => handleRefuserDocument(document.id)}
                                      className="bg-red-600 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-red-700 text-sm"
                                    >
                                      ✗ Refuser
                                    </button>
                                  </div>
                                )}
                                {(document.statut_validation === 'VALIDE' || document.statut_validation === 'REFUSE') && (
                                  <button
                                    onClick={() => handleAnnulerValidationDocument(document.id)}
                                    className="w-full sm:w-auto bg-gray-500 text-white px-4 py-2 sm:px-3 sm:py-1 rounded hover:bg-gray-600 text-sm"
                                  >
                                    ↺ Annuler la validation
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Modal d'informations du responsable légal */}
      <ParentInfoModal
        isOpen={showParentInfoModal}
        onClose={() => setShowParentInfoModal(false)}
        inscription={inscription}
      />
    </div>
  );
};

export default AdminInscriptionDetail;
